import { HttpClient, HttpHeaders, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { defer as observableDefer, Observable, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { timeout, map, catchError } from 'rxjs/operators';
import { timeoutError } from '../../models/timeout-error';
import { TimeoutLimit } from '../../models/timeout-limit';
import { GlobalEvent, GlobalEventService } from '../global-event-service/global-event.service';
import { MileagePlanActivityLookupResponse } from '../../dtos/response/mileage-plan-activity-response/mileage-plan-activity-lookup-response';
import { MileagePlanActivityLookupStatus } from '../../dtos/response/mileage-plan-activity-response/mileage-plan-activity-lookup-status';
import { MileagePlanActivityResponseDetails } from '../../dtos/response/mileage-plan-activity-response/mileage-plan-activity-response-details';
import { MileagePlanActivityRange } from '../../dtos/request/mileage-plan-activity-request/mileage-plan-activity-range';

export interface MileagePlanActivityServiceAPI {
  getMileagePlanActivities(
    mileagePlanNumber: string,
    range: MileagePlanActivityRange,
    backgroundLookup: boolean
  ): Observable<MileagePlanActivityLookupResponse>;
}

const PAGE_SIZE = 1000;
const NO_DATA_STATUS = 'No Activities for the member.';

@Injectable({
  providedIn: 'root',
})
export class MileagePlanActivityService implements MileagePlanActivityServiceAPI {
  private readonly http = inject(HttpClient);
  private readonly eventService = inject(GlobalEventService);

  public getMileagePlanActivities(
    mileagePlanNumber: string,
    range: MileagePlanActivityRange,
    backgroundLookup: boolean = false
  ): Observable<MileagePlanActivityLookupResponse> {
    const options = {
      headers: new HttpHeaders({
        background: backgroundLookup ? 'true' : 'false',
      }),
    };
    const queryParams = [
      `mileagePlanNumber=${mileagePlanNumber}`,
      `activityType=${MileagePlanActivityRange.all}`,
      `activityRange=${range}`,
      `limit=${PAGE_SIZE}`,
    ].join('&');
    return this.http.get<MileagePlanActivityResponseDetails>(`api/activities?${queryParams}`, options).pipe(
      timeout({
        each: TimeoutLimit.LONG,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((details) => {
        if (details.activities?.length === 1) {
          if (details.activities[0].status === NO_DATA_STATUS) {
            return {
              details,
              status: MileagePlanActivityLookupStatus.NO_DATA_RETURNED,
              mileagePlanNumber,
              currentActivityRange: range,
            } as MileagePlanActivityLookupResponse;
          } else if (details.activities[0].errorCode && details.activities[0].errorCode !== '') {
            return {
              details,
              status: MileagePlanActivityLookupStatus.SYSTEM_FAILURE,
              mileagePlanNumber,
              error: details.activities[0].errorMessage,
              currentActivityRange: range,
            };
          }
        }

        // Default case: return SUCCESS for any other scenario (0 activities, multiple activities, or single activity without error)
        return {
          details,
          status: MileagePlanActivityLookupStatus.SUCCESS,
          mileagePlanNumber,
          currentActivityRange: range,
        } as MileagePlanActivityLookupResponse;
      }),
      catchError((err) => {
        if (timeoutError.statusText === err.statusText || err?.status === HttpStatusCode.GatewayTimeout) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
          return observableOf({
            status: MileagePlanActivityLookupStatus.TIMEOUT,
            mileagePlanNumber,
            error: 'Timeout error',
            currentActivityRange: range,
          });
        }
        if (404 === err.status) {
          return observableOf({
            status: MileagePlanActivityLookupStatus.NO_DATA_RETURNED,
            mileagePlanNumber,
            error: err.error,
            currentActivityRange: range,
          });
        }
        if (400 === err.status) {
          return observableOf({
            status: MileagePlanActivityLookupStatus.BAD_REQUEST,
            mileagePlanNumber,
            error: JSON.stringify(err.error),
            currentActivityRange: range,
          });
        }

        return observableOf({
          status: MileagePlanActivityLookupStatus.SYSTEM_FAILURE,
          mileagePlanNumber,
          error: err.statusText,
          currentActivityRange: range,
        });
      })
    );
  }
}
